// One draft, opened from Drafts (pages/Applications.tsx): the job up top, its
// details on the left and the questions beside them, each answered in place
// and saved as you type.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ExternalLink, Pencil, Plus, Send, Trash2 } from "lucide-react";
import { auth, db } from "../firebase";
import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { cn } from "../lib/utils";
import { useNavbar } from "../pages/DashboardLayout";
import BackLink from "./BackLink";
import { CompanyLogo, DANGER_ITEM, RowMenu } from "./dreamShared";
import { MENU_ITEM } from "./applicationShared";
import { DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { MELT_BUTTON, MELT_BUTTON_QUIET, MELT_BUTTON_WHITE } from "./ui/melt";
import { isUrgent, shortDate, toMs, type DraftSource } from "../utils/drafts";

type SavedApplication = {
  id: string;
  company: string;
  role: string;
  jobUrl?: string;
  location?: string;
  /** yyyy-mm-dd */
  deadline?: string;
  source?: DraftSource;
  createdAt?: any;
};

type ApplicationQuestion = {
  id: string;
  applicationId: string;
  question: string;
  answer: string;
  wordLimit?: number;
  order: number;
};

type ApplicationWorkspaceProps = {
  application: SavedApplication;
  questions: ApplicationQuestion[];
  /** The company's icon (Drafts' brandOf) */
  brand?: { domain?: string; logo?: string };
  /** Mark applied: moves it to Applications */
  onComplete: () => void;
  onBack: () => void;
  /** Already applied (a past draft): no Mark applied */
  submitted?: boolean;
  submitting?: boolean;
};

const PANEL = "rounded-2xl border border-melt-line bg-melt-paper";
const SAVE_AFTER_MS = 700;

const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

const questionsRef = () => {
  const user = auth.currentUser;
  return user ? collection(db, "users", user.uid, "applicationQuestions") : null;
};

/** A textarea that grows with what's written in it */
function useAutoGrow(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return ref;
}

export default function ApplicationWorkspace({
  application,
  questions,
  brand,
  onComplete,
  onBack,
  submitted = false,
  submitting = false,
}: ApplicationWorkspaceProps) {
  const { setNavbarActions } = useNavbar();
  const answered = questions.filter((q) => q.answer.trim()).length;
  const ready = questions.length > 0 && answered === questions.length;
  const status = submitted ? "Applied" : ready ? "Ready to submit" : "Drafting";
  const { domain, logo } = brand || {};

  // Just the page title up top, like every page; the way back is on the page
  useEffect(() => {
    setNavbarActions(
      <div className="flex w-full min-w-0 items-center font-grotesk">
        <h1 className="truncate text-[22px] font-semibold leading-none tracking-[-.03em] text-melt-text1">Drafts</h1>
      </div>,
    );
  }, [setNavbarActions]);

  useEffect(() => () => setNavbarActions(null), [setNavbarActions]);

  return (
    <div className="flex w-full flex-col gap-4 pb-8 font-grotesk">
      <BackLink to="Drafts" onClick={onBack} className="-mb-1" />

      <header className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <CompanyLogo name={application.company} domain={domain} logo={logo} size={44} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[22px] font-semibold leading-tight tracking-[-.02em] text-melt-text1">
            {application.company}
          </h2>
          <p className="mt-0.5 truncate text-[13.5px] text-melt-text2">
            {application.role || "Role not added"}
            <span className="text-melt-text3">
              {" · "}
              {status}
            </span>
          </p>
        </div>
        {questions.length > 0 && (
          <span className="whitespace-nowrap font-plex text-xs text-melt-text3">
            {answered} of {questions.length} written
          </span>
        )}
        {!submitted && (
          <button
            type="button"
            onClick={onComplete}
            disabled={submitting}
            title="Moves it to Applications"
            className={cn(MELT_BUTTON_WHITE, "h-10 whitespace-nowrap rounded-[11px] px-4 text-sm")}
          >
            <Send size={15} strokeWidth={1.75} />
            {submitting ? "Moving…" : "Mark applied"}
          </button>
        )}
      </header>

      <div className="grid w-full items-start gap-4 lg:grid-cols-[minmax(240px,300px)_minmax(0,1fr)]">
        <JobPanel application={application} />

        <div className="flex min-w-0 flex-col gap-3">
          {questions.map((q, i) => (
            <QuestionCard key={q.id} question={q} index={i} />
          ))}
          <AddQuestion order={questions.length} applicationId={application.id} first={questions.length === 0} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the job

const applicationRef = (id: string) => {
  const user = auth.currentUser;
  return user ? doc(db, "users", user.uid, "writtenApplications", id) : null;
};

const FIELD =
  "block w-full rounded-[8px] border border-transparent bg-transparent px-2 py-1 text-[13.5px] leading-snug text-melt-text1 outline-none transition-colors placeholder:text-melt-text3 hover:bg-white/70 focus:border-melt-caramel/60 focus:bg-white focus:shadow-[0_0_0_3px_rgba(217,136,41,.12)]";

/** One detail of the job, edited in place and saved when you leave it. */
function JobField({
  label,
  value,
  placeholder,
  type = "text",
  required = false,
  className,
  onSave,
}: {
  label: string;
  value: string;
  placeholder: string;
  type?: "text" | "date" | "url";
  required?: boolean;
  className?: string;
  onSave: (value: string) => Promise<void>;
}) {
  const [text, setText] = useState(value);
  const [error, setError] = useState(false);
  const editing = useRef(false);
  const dropped = useRef(false);

  // Saves elsewhere show up, unless this one is mid-edit
  useEffect(() => {
    if (!editing.current) setText(value);
  }, [value]);

  const save = async () => {
    editing.current = false;
    if (dropped.current) {
      dropped.current = false;
      return;
    }
    const next = text.trim();
    if (next === value || (required && !next)) {
      setText(value);
      return;
    }
    try {
      await onSave(next);
      setError(false);
    } catch (e) {
      console.error(`Error saving ${label}:`, e);
      setError(true);
    }
  };

  return (
    <label className="block">
      <span className="px-2 text-[12px] text-melt-text3">{label}</span>
      <input
        type={type}
        value={text}
        placeholder={placeholder}
        onFocus={() => (editing.current = true)}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            dropped.current = true;
            setText(value);
            e.currentTarget.blur();
          }
        }}
        className={cn(FIELD, "mt-0.5", className)}
      />
      {error && <span className="mt-1 block px-2 text-[12px] text-[#A5492E]">Couldn’t save that.</span>}
    </label>
  );
}

function JobPanel({ application }: { application: SavedApplication }) {
  const saved = shortDate(toMs(application.createdAt));
  const urgent = isUrgent(application.deadline);

  const save = (field: keyof SavedApplication) => async (value: string) => {
    const ref = applicationRef(application.id);
    if (ref) await updateDoc(ref, { [field]: value });
  };

  return (
    <aside className={cn(PANEL, "flex flex-col gap-2.5 p-3 lg:sticky lg:top-0")}>
      <JobField label="Company" value={application.company} placeholder="Company" required onSave={save("company")} />
      <JobField label="Role" value={application.role} placeholder="Add the role" required onSave={save("role")} />
      <JobField label="Location" value={application.location || ""} placeholder="Add a location" onSave={save("location")} />
      <JobField
        label="Deadline"
        type="date"
        value={application.deadline || ""}
        placeholder="No deadline"
        className={cn(urgent && "font-medium text-[#A5492E]", !application.deadline && "text-melt-text3")}
        onSave={save("deadline")}
      />
      <div>
        <JobField
          label="Posting"
          type="url"
          value={application.jobUrl || ""}
          placeholder="Paste the link"
          className="truncate"
          onSave={save("jobUrl")}
        />
        {application.jobUrl && (
          <a
            href={application.jobUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex w-fit items-center gap-1 px-2 text-[13px] font-medium text-melt-text2 transition-colors hover:text-melt-text1"
          >
            View posting
            <ExternalLink size={12} strokeWidth={1.75} />
          </a>
        )}
      </div>
      {saved && <p className="mt-1 border-t border-melt-hair px-2 pt-3 font-plex text-[11px] text-melt-text3">Saved {saved}</p>}
    </aside>
  );
}

// ---------------------------------------------------------------- questions

function WordCount({ words, limit }: { words: number; limit?: number | null }) {
  const over = Boolean(limit && words > limit);
  return (
    <span className={cn("whitespace-nowrap font-plex text-[11px]", over ? "font-medium text-[#A5492E]" : "text-melt-text3")}>
      {words}
      {limit ? ` / ${limit}` : ""} words
    </span>
  );
}

function QuestionNumber({ n, done }: { n: number; done: boolean }) {
  return (
    <span
      className={cn(
        "mt-px grid h-5 w-5 flex-none place-items-center rounded-full font-plex text-[10.5px] font-medium",
        done ? "bg-melt-caramel text-white" : "bg-melt-text1/[.08] text-melt-text2",
      )}
    >
      {n}
    </span>
  );
}

/** One question: its answer is typed straight in and saved after a pause. */
function QuestionCard({ question, index }: { question: ApplicationQuestion; index: number }) {
  const [answer, setAnswer] = useState(question.answer);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(false);
  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const ref = useAutoGrow(answer);

  // Someone else's save (another tab) shows up, unless this one is mid-edit
  useEffect(() => {
    if (pending.current === null) setAnswer(question.answer);
  }, [question.answer]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const text = pending.current;
    const col = questionsRef();
    if (text === null || !col) return;
    try {
      await updateDoc(doc(col, question.id), { answer: text });
      if (pending.current === text) pending.current = null;
      setError(false);
    } catch (e) {
      console.error("Error saving answer:", e);
      setError(true);
    }
  }, [question.id]);

  // Leaving the page still saves the last few words
  useEffect(() => () => void flush(), [flush]);

  const change = (text: string) => {
    setAnswer(text);
    pending.current = text;
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_AFTER_MS);
  };

  const remove = async () => {
    const col = questionsRef();
    if (!col) return;
    try {
      await deleteDoc(doc(col, question.id));
    } catch (e) {
      console.error("Error deleting question:", e);
    }
  };

  const words = countWords(answer);
  const over = Boolean(question.wordLimit && words > question.wordLimit);

  return (
    <div className={cn(PANEL, "group p-4 transition-colors focus-within:border-melt-text1/25")}>
      {editing ? (
        <QuestionFields
          initial={question}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (text, limit) => {
            const col = questionsRef();
            if (!col) return;
            await updateDoc(doc(col, question.id), {
              question: text,
              wordLimit: limit ?? null,
            });
            setEditing(false);
          }}
        />
      ) : (
        <div className="flex items-start gap-3">
          <QuestionNumber n={index + 1} done={Boolean(answer.trim())} />
          <h3 className="min-w-0 flex-1 text-[14.5px] font-semibold leading-snug text-melt-text1">{question.question}</h3>
          <WordCount words={words} limit={question.wordLimit} />
          <div className="-mr-1 -mt-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <RowMenu label={`Options for question ${index + 1}`}>
              <DropdownMenuItem onSelect={() => setEditing(true)} className={MENU_ITEM}>
                <Pencil size={15} strokeWidth={1.75} />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-melt-hair" />
              <DropdownMenuItem onSelect={remove} className={DANGER_ITEM}>
                <Trash2 size={15} strokeWidth={1.75} />
                Delete
              </DropdownMenuItem>
            </RowMenu>
          </div>
        </div>
      )}

      <textarea
        ref={ref}
        value={answer}
        onChange={(e) => change(e.target.value)}
        onBlur={flush}
        placeholder="Start writing…"
        aria-label={`Answer to question ${index + 1}`}
        rows={3}
        className={cn(
          "mt-3 block w-full resize-none overflow-hidden rounded-[10px] border border-transparent bg-white px-3.5 py-3 text-[14px] leading-relaxed text-melt-text1 outline-none transition-shadow placeholder:text-melt-text3 focus:border-melt-caramel/60 focus:shadow-[0_0_0_3px_rgba(217,136,41,.12)]",
          over && "border-[#A5492E]/40",
        )}
      />
      {error && <p className="mt-2 text-[12.5px] text-[#A5492E]">Couldn’t save that. It’ll try again when you keep typing.</p>}
    </div>
  );
}

/** Question text and word limit: adding one, or editing one in place. */
function QuestionFields({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: ApplicationQuestion;
  submitLabel: string;
  onSubmit: (text: string, limit: number | null) => Promise<void>;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial?.question ?? "");
  const [limit, setLimit] = useState(initial?.wordLimit?.toString() ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const ref = useAutoGrow(text);

  const submit = async () => {
    if (saving) return;
    if (!text.trim()) {
      setError("Add the question first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit(text.trim(), parseInt(limit) > 0 ? parseInt(limit) : null);
    } catch (e) {
      console.error("Error saving question:", e);
      setError("Couldn’t save. Try again.");
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
      // Clicking away closes it, unless something new was written
      onBlur={(e) => {
        if (saving || e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        const untouched = initial
          ? text.trim() === initial.question && limit === (initial.wordLimit?.toString() ?? "")
          : !text.trim();
        if (untouched) onCancel();
      }}
      className="flex flex-col gap-2.5"
    >
      <textarea
        ref={ref}
        autoFocus
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setError("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Paste a question from the application"
        aria-label="Question"
        rows={1}
        className="block w-full resize-none overflow-hidden rounded-[10px] border border-melt-hair bg-white px-3.5 py-2.5 text-[14px] font-semibold leading-snug text-melt-text1 outline-none placeholder:font-normal placeholder:text-melt-text3 focus:border-melt-caramel/60 focus:shadow-[0_0_0_3px_rgba(217,136,41,.12)]"
      />
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-[13px] text-melt-text2">
          Word limit
          <input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="None"
            className="h-8 w-20 rounded-[8px] border border-melt-hair bg-white px-2.5 text-[13px] text-melt-text1 outline-none placeholder:text-melt-text3 focus:border-melt-caramel/60"
          />
        </label>
        {error && <span className="text-[12.5px] text-[#A5492E]">{error}</span>}
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={onCancel} className={cn(MELT_BUTTON_QUIET, "h-8 px-3 text-[13px]")}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={cn(MELT_BUTTON, "h-8 rounded-[9px] px-3.5 text-[13px]")}>
            {saving ? "Saving…" : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

function AddQuestion({ applicationId, order, first }: { applicationId: string; order: number; first: boolean }) {
  const [open, setOpen] = useState(first);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-10 w-fit items-center gap-1.5 rounded-[10px] px-3 text-[13.5px] font-medium text-melt-text2 transition-colors hover:bg-melt-text1/[.05] hover:text-melt-text1"
      >
        <Plus size={15} strokeWidth={1.75} />
        Add a question
      </button>
    );
  }

  return (
    <div className={cn(PANEL, "p-4")}>
      {first && (
        <p className="mb-3 text-[13px] leading-relaxed text-melt-text2">
          Copy the questions from the application, one at a time. You’ll write each answer right under it.
        </p>
      )}
      <QuestionFields
        submitLabel="Add question"
        onCancel={() => setOpen(false)}
        onSubmit={async (text, limit) => {
          const col = questionsRef();
          if (!col) return;
          await addDoc(col, {
            applicationId,
            question: text,
            answer: "",
            order,
            ...(limit ? { wordLimit: limit } : {}),
            createdAt: serverTimestamp(),
          });
          setOpen(false);
        }}
      />
    </div>
  );
}
